import React, { useEffect, useState, useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ExternalLink, Clock, Target, Book, Users, Check, Star, ArrowRight, ChevronDown, ChevronUp, Award, TrendingUp, Eye, Headphones, Hand } from 'lucide-react';
import PageSEO from '../components/SEO/PageSEO';

// Composant pour les particules flottantes
const FloatingParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(15)].map((_, i) => (
        <div
          key={i}
          className="absolute w-2 h-2 bg-white/20 rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 10}s`,
            animationDuration: `${8 + Math.random() * 4}s`
          }}
        />
      ))}
      {[...Array(10)].map((_, i) => (
        <div
          key={`circle-${i}`}
          className="absolute w-1 h-1 bg-[#9a6ebd]/30 rounded-full animate-float-slow"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 15}s`,
            animationDuration: `${12 + Math.random() * 6}s`
          }}
        />
      ))}
    </div>
  );
};

// Hook personnalisé pour les animations au scroll
const useScrollAnimation = () => {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observer toutes les sections avec un ID
    const sections = document.querySelectorAll('section[id]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return visibleSections;
};

const CoursParticuliersPage = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const visibleSections = useScrollAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQ enrichie
  const faqs = [
    {
      question: 'Comment se déroulent les cours en visio ?',
      answer: 'Les cours se déroulent sur la plateforme Wooskill, qui offre tous les outils nécessaires pour un apprentissage efficace : tableau blanc interactif, partage d\'écran, chat, etc. Vous recevez un lien de connexion avant chaque séance.'
    },
    {
      question: 'Comment se déroule la première séance ?',
      answer: 'La première séance commence par un bilan personnalisé de 15 minutes pour identifier votre niveau, vos difficultés et vos objectifs. Nous adaptons ensuite le contenu du cours à votre profil d\'apprentissage (visuel, auditif ou kinesthésique) pour maximiser votre compréhension.'
    },
    {
      question: 'Puis-je choisir mon professeur ?',
      answer: 'Oui ! Vous pouvez choisir entre Marion (spécialiste sociologie) et Brandon (spécialiste économie) selon vos besoins. Nous vous orienterons vers le professeur le mieux adapté à votre demande lors de la réservation.'
    },
    {
      question: 'Y a-t-il des devoirs ou des exercices entre les cours ?',
      answer: 'Nous vous proposons des exercices personnalisés entre les séances pour consolider vos acquis. Ces exercices sont optionnels mais fortement recommandés pour maximiser vos progrès. Vous recevez également des fiches de révision adaptées à votre niveau.'
    },
    {
      question: 'Comment suivre mes progrès ?',
      answer: 'Après chaque cours, vous recevez un bilan détaillé de vos progrès avec vos points forts, axes d\'amélioration et objectifs pour la séance suivante. Nous suivons également l\'évolution de vos notes et vous donnons des conseils personnalisés.'
    },
    {
      question: 'Quels sont les prérequis techniques pour les cours en visio ?',
      answer: 'Vous avez besoin d\'une connexion internet stable, d\'un ordinateur/tablette/smartphone avec caméra et microphone. La plateforme Wooskill fonctionne directement dans votre navigateur, aucune installation n\'est nécessaire.'
    },
    {
      question: 'Puis-je annuler ou reporter un cours ?',
      answer: 'Oui, vous pouvez annuler ou reporter un cours jusqu\'à 24h avant le début de la séance. Dans ce cas, vous serez intégralement remboursé ou pourrez choisir un autre créneau.'
    },
    {
      question: 'Comment sont personnalisés les cours ?',
      answer: 'Avant chaque première séance, nous évaluons votre niveau et vos besoins spécifiques. Le contenu est ensuite adapté pour répondre à vos objectifs, que ce soit pour renforcer certaines notions ou préparer des examens.'
    }
  ];

  // Schéma JSON-LD pour le service
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Cours Particuliers de SES",
    "description": "Cours particuliers de Sciences Économiques et Sociales personnalisés en visioconférence pour les élèves de Première et Terminale.",
    "provider": {
      "@type": "Organization",
      "name": "Comprendre les SES",
      "image": "https://cours-ses.fr/Logo SES.png"
    },
    "offers": {
      "@type": "Offer",
      "price": "25.00",
      "priceCurrency": "EUR",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "25.00",
        "priceCurrency": "EUR",
        "unitText": "HOUR"
      }
    },
    "serviceType": "Cours particuliers en ligne",
    "areaServed": "France",
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": "https://www.wooskill.com/fr/catalog/17929",
      "servicePhone": "+33600000000",
      "serviceSmsNumber": "+33600000000"
    },
    "termsOfService": "https://cours-ses.fr/cgv"
  };

  // FAQ pour le schéma structuré
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageSEO
        title="Cours Particuliers SES en Visio | Première et Terminale"
        description="Cours particuliers de SES personnalisés en visioconférence pour les élèves de Première et Terminale. Progressez à votre rythme avec Brandon & Marion !"
        path="/cours-particuliers"
        image="https://cours-ses.fr/Logo SES.png"
        schema={schema}
      />
      <Navbar />
      <main>
        {/* Hero Section */}
        <section 
          id="hero"
          className={`relative bg-gradient-to-b from-[#9a6ebd]/20 via-[#9a6ebd]/10 to-white py-16 transition-all duration-1000 ${
            visibleSections.has('hero') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-5'
          }`}
        >
          <FloatingParticles />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-[#323c5f] mb-4">
                Cours Particuliers de SES
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Gagnez jusqu'à 4 points sur votre moyenne en SES grâce à une méthode adaptée à votre profil d'apprentissage.
              </p>

              <a
                href="https://www.wooskill.com/fr/catalog/17929"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-[#9a6ebd] text-white px-8 py-4 rounded-lg hover:bg-[#8a5dad] transition-all duration-300 text-lg font-semibold transform hover:scale-105"
              >
                Voir nos offres
                <ArrowRight className="ml-2" size={20} />
              </a>
            </div>
          </div>
        </section>
        <div className="border-b border-[#9a6ebd]/10" />

        {/* Process Section */}
        <section 
          id="process"
          className={`py-16 bg-gradient-to-br from-white via-[#9a6ebd]/5 to-white relative overflow-hidden transition-all duration-1000 delay-200 ${
            visibleSections.has('process') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-5'
          }`}
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNMzAgMzBtLTI4IDBhMjggMjggMCAxIDAgNTYgMCAyOCAyOCAwIDEgMC01NiAwIiBzdHJva2U9IiM5YTZlYmQiIHN0cm9rZS13aWR0aD0iLjUiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-50"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-[#9a6ebd]/20 mb-12">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-[#9a6ebd] rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Star className="text-white w-10 h-10 md:w-12 md:h-12" />
                </div>
                <div className="text-center md:text-left">
                  <h2 className="text-2xl md:text-3xl font-bold text-[#323c5f] mb-2">
                    Comment ça marche ?
                  </h2>
                  <p className="text-gray-600 md:text-lg">
                    Tous nos cours sont gérés via la plateforme Wooskill, un outil professionnel 
                    spécialisé dans les cours en ligne. Un processus simple et efficace en 3 étapes pour maximiser votre réussite :
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 relative">
              <div className="bg-white p-6 rounded-xl shadow-lg border border-[#ceace8] relative transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <Clock className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-[#323c5f] mb-2">
                  1. Réservez votre créneau
                </h3>
                <p className="text-gray-600">
                  Choisissez la date et l'heure qui vous conviennent sur Wooskill. 
                  Indiquez vos besoins et le chapitre à travailler via le chat pour un cours 100% personnalisé.
                </p>
                {/* Flèche vers la droite */}
                <div className="hidden md:block absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                  <div className="w-8 h-8 bg-[#9a6ebd] rounded-full flex items-center justify-center animate-pulse">
                    <ArrowRight className="text-white" size={20} />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border border-[#ceace8] relative transform transition-all duration-500 delay-100 hover:-translate-y-2 hover:shadow-xl">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <Target className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-[#323c5f] mb-2">
                  2. Suivez votre cours
                </h3>
                <p className="text-gray-600">
                  Rejoignez la visioconférence en un clic depuis votre espace Wooskill. 
                  Bénéficiez d'un accompagnement adapté à votre style d'apprentissage pour des résultats optimaux.
                </p>
                {/* Flèche vers la droite */}
                <div className="hidden md:block absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                  <div className="w-8 h-8 bg-[#9a6ebd] rounded-full flex items-center justify-center animate-pulse">
                    <ArrowRight className="text-white" size={20} />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border border-[#ceace8] transform transition-all duration-500 delay-200 hover:-translate-y-2 hover:shadow-xl">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <Book className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-[#323c5f] mb-2">
                  3. Recevez vos supports
                </h3>
                <p className="text-gray-600">
                  Après le cours, vous recevez une fiche de sytnhèse et des exercices 
                  ciblés pour consolider vos acquis et maintenir votre progression.
                </p>
              </div>
            </div>
          </div>
        </section>
        <div className="border-b border-[#9a6ebd]/10" />

        {/* Pricing Section */}
        <section 
          id="pricing"
          className={`py-16 relative overflow-hidden transition-all duration-1000 delay-400 ${
            visibleSections.has('pricing') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-5'
          }`}
        >
          <FloatingParticles />
          <div className="absolute inset-0 bg-gradient-to-br from-[#9a6ebd]/10 to-white"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBIMHoiLz48cGF0aCBkPSJNMjAgMjBtLTE4IDBhMTggMTggMCAxIDAgMzYgMCAxOCAxOCAwIDEgMC0zNiAwIiBzdHJva2U9IiM5YTZlYmQiIHN0cm9rZS13aWR0aD0iLjUiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#323c5f] mb-4">
                Nos Formules
              </h2>
              <p className="text-xl text-gray-600">
                Des formules conçues pour vous faire exceller, quel que soit votre objectif.
              </p>
            </div>

            {/* Terminale */}
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-[#323c5f] text-center mb-8">
                Cours Particuliers Terminale
              </h2>
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {/* 1h */}
                <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 flex flex-col transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#323c5f]">Cours 1h</h3>
                    <div className="text-2xl font-bold text-[#9a6ebd]">25€</div>
                  </div>
                  <ul className="space-y-3 flex-grow">
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Cours individuel en visio
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Support de cours personnalisé
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Exercices corrigés
                    </li>
                  </ul>
                  <a
                    href="https://www.wooskill.com/fr/checkout-ws/dbc17174-6211-4ea0-9ad9-b9fdeccbfa6d"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 block w-full bg-white text-[#9a6ebd] border-2 border-[#9a6ebd] px-6 py-3 rounded-lg hover:bg-[#9a6ebd]/5 transition-all duration-300 text-center font-medium transform hover:scale-105"
                  >
                    Réserver
                    <ExternalLink className="inline-block ml-2" size={16} />
                  </a>
                </div>

                {/* 2h */}
                <div className="bg-white rounded-xl shadow-lg p-8 relative border-2 border-[#9a6ebd] flex flex-col transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#9a6ebd] text-white px-4 py-1 rounded-full text-sm font-medium">
                      Recommandé
                    </span>
                  </div>
                  <div className="absolute -top-4 right-4">
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#323c5f]">Cours 2h</h3>
                    <div className="flex flex-col items-end">
                      <div className="text-2xl font-bold text-[#9a6ebd]">50€</div>
                    </div>
                  </div>
                  <ul className="space-y-3 flex-grow">
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Cours individuel en visio
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Support de cours personnalisé
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Exercices corrigés
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Plus de temps pour approfondir
                    </li>
                  </ul>
                  <a
                    href="https://www.wooskill.com/fr/checkout-ws/bed60e6e-2e3f-4353-9577-42b2c46936c5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 block w-full bg-[#9a6ebd] text-white px-6 py-3 rounded-lg hover:bg-[#8a5dad] transition-all duration-300 text-center font-medium transform hover:scale-105"
                  >
                    Réserver
                    <ExternalLink className="inline-block ml-2" size={16} />
                  </a>
                </div>
              </div>
            </div>

            {/* Première */}
            <div>
              <h2 className="text-2xl font-bold text-[#323c5f] text-center mb-8">
                Cours Particuliers Première
              </h2>
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {/* 1h */}
                <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 flex flex-col transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#323c5f]">Cours 1h</h3>
                    <div className="text-2xl font-bold text-[#9a6ebd]">25€</div>
                  </div>
                  <ul className="space-y-3 flex-grow">
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Cours individuel en visio
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Support de cours personnalisé
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Exercices corrigés
                    </li>
                  </ul>
                  <a
                    href="https://www.wooskill.com/fr/checkout-ws/1495cccd-0207-4e8f-9fb8-6dc461e6cdda"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 block w-full bg-white text-[#9a6ebd] border-2 border-[#9a6ebd] px-6 py-3 rounded-lg hover:bg-[#9a6ebd]/5 transition-all duration-300 text-center font-medium transform hover:scale-105"
                  >
                    Réserver
                    <ExternalLink className="inline-block ml-2" size={16} />
                  </a>
                </div>

                {/* 2h */}
                <div className="bg-white rounded-xl shadow-lg p-8 relative border-2 border-[#9a6ebd] flex flex-col transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#9a6ebd] text-white px-4 py-1 rounded-full text-sm font-medium">
                      Recommandé
                    </span>
                  </div>
                  <div className="absolute -top-4 right-4">
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#323c5f]">Cours 2h</h3>
                    <div className="flex flex-col items-end">
                      <div className="text-2xl font-bold text-[#9a6ebd]">50€</div>
                    </div>
                  </div>
                  <ul className="space-y-3 flex-grow">
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Cours individuel en visio
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Support de cours personnalisé
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Exercices corrigés
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Plus de temps pour approfondir
                    </li>
                  </ul>
                  <a
                    href="https://www.wooskill.com/fr/checkout-ws/bed60e6e-2e3f-4353-9577-42b2c46936c5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 block w-full bg-[#9a6ebd] text-white px-6 py-3 rounded-lg hover:bg-[#8a5dad] transition-all duration-300 text-center font-medium transform hover:scale-105"
                  >
                    Réserver
                    <ExternalLink className="inline-block ml-2" size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div className="border-b border-[#9a6ebd]/10" />

        {/* FAQ Section */}
        <section 
          id="faq"
          className={`py-16 bg-white transition-all duration-1000 delay-600 ${
            visibleSections.has('faq') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-5'
          }`}
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#323c5f] mb-4">
                Questions Fréquentes
              </h2>
              <p className="text-xl text-gray-600">
                Tout ce que vous devez savoir sur nos cours particuliers
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden border border-[#ceace8] transform transition-all duration-300 hover:shadow-xl"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-[#9a6ebd]/5 transition-colors"
                  >
                    <span className="font-medium text-[#323c5f] pr-4">{faq.question}</span>
                    {openFaqIndex === index ? (
                      <ChevronUp className="text-[#9a6ebd] flex-shrink-0" size={20} />
                    ) : (
                      <ChevronDown className="text-[#9a6ebd] flex-shrink-0" size={20} />
                    )}
                  </button>
                  
                  <div
                    className={`px-6 transition-all duration-300 ease-in-out overflow-hidden ${
                      openFaqIndex === index ? 'max-h-96 pb-4' : 'max-h-0'
                    }`}
                  >
                    <div className="bg-[#9a6ebd]/5 p-4 rounded-lg">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        <div className="border-b border-[#9a6ebd]/10" />

        {/* CTA Section */}
        <section 
          id="cta"
          className={`py-16 relative overflow-hidden transition-all duration-1000 delay-800 ${
            visibleSections.has('cta') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-5'
          }`}
        >
          <FloatingParticles />
          <div className="absolute inset-0 bg-gradient-to-r from-[#9a6ebd]/20 via-[#9a6ebd]/10 to-[#9a6ebd]/20"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTIiIGhlaWdodD0iNTIiIHZpZXdCb3g9IjAgMCA1MiA1MiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNTJ2NTJIMHoiLz48cGF0aCBkPSJNMjYgMjZtLTI1IDBhMjUgMjUgMCAxIDAgNTAgMCAyNSAyNSAwIDEgMC01MCAwIiBzdHJva2U9IiM5YTZlYmQiIHN0cm9rZS13aWR0aD0iLjUiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-[#9a6ebd]/20">
              <h2 className="text-3xl font-bold text-[#323c5f] mb-6">
                Prêt à transformer vos résultats en SES ?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Réservez votre premier cours et débloquez votre plein potentiel dès aujourd'hui !
              </p>
              <a
                href="https://www.wooskill.com/fr/catalog/17929"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-[#9a6ebd] text-white px-8 py-4 rounded-lg hover:bg-[#8a5dad] transition-all duration-300 text-lg font-semibold transform hover:scale-105"
              >
                <Clock className="mr-2" size={24} />
                Réserver un créneau
                <ExternalLink className="ml-2" size={20} />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
            opacity: 0.3;
          }
          25% {
            transform: translateY(-10px) translateX(5px);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-5px) translateX(-5px);
            opacity: 0.4;
          }
          75% {
            transform: translateY(-15px) translateX(3px);
            opacity: 0.7;
          }
        }

        @keyframes float-slow {
          0%, 100% {
            transform: translateY(0px) translateX(0px) scale(1);
            opacity: 0.2;
          }
          33% {
            transform: translateY(-20px) translateX(10px) scale(1.1);
            opacity: 0.4;
          }
          66% {
            transform: translateY(-10px) translateX(-8px) scale(0.9);
            opacity: 0.3;
          }
        }

        .animate-float {
          animation: float 8s ease-in-out infinite;
        }

        .animate-float-slow {
          animation: float-slow 12s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default CoursParticuliersPage;